import React, { useCallback } from 'react';
import { useCanvasAnimation } from './useCanvasAnimation';

export interface WaveSimulationProps {
  size: number;
  frequency: number;
  amplitude: number;
  color: string;
  waveCount: number;
}

export function WaveSimulation({
  size,
  frequency,
  amplitude,
  color,
  waveCount,
}: WaveSimulationProps) {
  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, frameCount: number) => {
      ctx.clearRect(0, 0, size, size);
      
      const centerY = size / 2;
      
      // We can draw multiple waves slightly offset for a glowing/layered effect
      for (let w = 0; w < waveCount; w++) {
        ctx.beginPath();
        
        const phaseOffset = w * (Math.PI / waveCount);
        const speed = 0.05 + w * 0.01;
        const timeOffset = frameCount * speed;
        
        for (let x = 0; x <= size; x += 2) {
          // Normalize x from 0 to 1
          const nx = x / size;
          // Calculate y using sine
          const y = centerY + Math.sin(nx * frequency * Math.PI * 2 + timeOffset + phaseOffset) * amplitude;
          
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        ctx.strokeStyle = color;
        // Fade out secondary waves
        ctx.globalAlpha = 1 - (w / waveCount) * 0.5;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      ctx.globalAlpha = 1.0; // reset
    },
    [size, frequency, amplitude, color, waveCount]
  );

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ width: size, height: size }} className="relative flex items-center justify-center">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
